import { put, select, call, takeEvery, takeLeading } from "redux-saga/effects";
import { updateCompleted } from "~/EasyProjects/TaskDetails/updateItem/actions";
import { Entities } from "~/normalizr/entities";
import { BasePortfolio } from "~/normalizr/PortfoliosSchema";
import { portfolioWasCreated, portfolioWasDeleted, updatePortfolioField } from "~/Redux/portfolios/actions";
import { isPortfoliosDataLoaded, portfoliosSelector } from "~/Redux/portfolios/selectors";
import { appUrls } from "../../EasyProjectsCommon/AppUrls";
import { OAuthClient } from "../../EasyProjectsCommon/OAuthClient";
import { callApi } from "../Sagas/effects/callApi";
import { addPortfolio, loadPortfolios, updatePortfolios } from "./slice";

export function* rootPortfoliosSaga() {
    yield takeLeading(loadPortfolios, loadPortfoliosSaga);
    yield takeEvery(portfolioWasDeleted, reloadPortfolios);

    yield takeEvery(updateCompleted, function* (action) {
        // User might see portfolio just because this user can see project with this portfolio.
        // So if user moved this project to another portfolio then old portfolio might become invisible.
        if (action.data.url === appUrls.inlineEdit.changeProjectPortfolio) {
            yield reloadPortfolios();
        }
    });

    yield takeEvery(updatePortfolioField, handlePortfolioUpdate);
    yield takeEvery(addPortfolio, addPortfolioSaga);
}

export function* loadPortfoliosSaga(action: ReturnType<typeof loadPortfolios>) {
    const isLoaded: boolean = yield select(isPortfoliosDataLoaded);
    if (isLoaded && action.payload.forceReload !== true) {
        return;
    }

    const portfolios: BasePortfolio[] = yield callApi(({ ajaxOptions }) =>
        OAuthClient.postJson(appUrls.portfolio.getVisible, null, ajaxOptions)
    );

    yield put(updatePortfolios(portfolios, { normalize: true, overrideAll: true }));
}

function* reloadPortfolios() {
    let isPortfoliosLoaded: boolean = yield select(isPortfoliosDataLoaded);
    if (isPortfoliosLoaded) {
        yield put(loadPortfolios({ forceReload: true }));
    }
}

function* handlePortfolioUpdate({ payload }: ReturnType<typeof updatePortfolioField>) {
    const { id, value, fieldName } = payload;
    if (!isKnownField(fieldName)) {
        return;
    }

    const portfolios: Entities<BasePortfolio> = yield select(portfoliosSelector);
    const portfolio = portfolios?.[id];

    if (portfolio) {
        yield put(updatePortfolios([{ ...portfolio, [fieldName]: value }]));
    }
}

export function* addPortfolioSaga({
    payload: { name, callback, parentId, copyCustomFields },
}: ReturnType<typeof addPortfolio>) {
    const { Id } = yield callApi(({ ajaxOptions }) =>
        OAuthClient.postJson(appUrls.portfolio.create, { name, parentId, copyCustomFields }, ajaxOptions)
    );

    let isPortfoliosLoaded: boolean = yield select(isPortfoliosDataLoaded);
    if (isPortfoliosLoaded) {
        const createdPortfolio: BasePortfolio[] = yield callApi(({ ajaxOptions }) =>
            OAuthClient.postJson(appUrls.portfolio.getVisible, { Id }, ajaxOptions)
        );
        yield put(updatePortfolios(createdPortfolio));
    }

    yield put(portfolioWasCreated(parentId));

    yield call(() => callback?.(Id));
}

const isKnownField = (key: string) => {
    type BasePortfolioFields = { [key in keyof BasePortfolio]: true };
    const basePortfolioFields: BasePortfolioFields = { Id: true, Name: true, ParentId: true };
    return !!(basePortfolioFields as any)[key];
};
